import { useMemo, useState } from 'react';
import {
  CATEGORIES,
  SETTINGS,
  settingsVisible,
  type SettingDef,
  type Visibility,
} from '../settings/schema';
import {
  FILAMENT_PRESETS,
  PROCESS_PRESETS,
  PRINTER_PRESETS,
  deleteUserPreset,
  dirtyKeys,
  filamentKeysOf,
  findFilamentPreset,
  findProcessPreset,
  findPrinterPreset,
  loadUserPresets,
  processKeysOf,
  renameUserPreset,
  saveUserPreset,
  type Preset,
} from '../settings/presets';
import { store, useAppStore } from '../store/appStore';
import { toast } from '../ui/toast';

function SettingRow({
  def,
  value,
  onChange,
  dirty,
  onReset,
}: {
  def: SettingDef;
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
  dirty?: boolean;
  onReset?: () => void;
}) {
  return (
    <div className={`setting-row-wrap ${dirty ? 'dirty' : ''}`}>
      {dirty && (
        <button
          type="button"
          className="dirty-dot"
          title="Reset to preset"
          aria-label={`Reset ${def.label} to preset`}
          onClick={onReset}
        />
      )}
      {def.type === 'boolean' ? (
        <label className="setting-row">
          <span className="setting-label" title={def.description}>
            {def.label}
          </span>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        </label>
      ) : def.type === 'enum' ? (
        <label className="setting-row">
          <span className="setting-label" title={def.description}>
            {def.label}
          </span>
          <select value={String(value)} onChange={(e) => onChange(e.target.value)}>
            {def.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      ) : def.type === 'color' ? (
        <label className="setting-row">
          <span className="setting-label">{def.label}</span>
          <input type="color" value={String(value)} onChange={(e) => onChange(e.target.value)} />
        </label>
      ) : (
        <label className="setting-row">
          <span className="setting-label" title={def.description}>
            {def.label}
            {def.unit ? <em className="unit"> {def.unit}</em> : null}
          </span>
          <input
            type="number"
            value={Number(value)}
            min={def.min}
            max={def.max}
            step={def.step ?? 1}
            onChange={(e) =>
              onChange(def.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value))
            }
          />
        </label>
      )}
    </div>
  );
}

function PresetSelect({
  label,
  valueId,
  displayName,
  modified,
  options,
  testId,
  onPick,
}: {
  label: string;
  valueId: string;
  displayName: string;
  modified: boolean;
  options: { id: string; name: string; group?: string }[];
  testId: string;
  onPick: (id: string) => void;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, { id: string; name: string }[]>();
    for (const o of options) {
      const g = o.group || 'Built-in';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(o);
    }
    return map;
  }, [options]);

  return (
    <label className="preset-select" data-testid={testId}>
      <span className="preset-label">{label}</span>
      <select
        value={valueId}
        aria-label={label}
        onChange={(e) => onPick(e.target.value)}
      >
        {[...groups.entries()].map(([g, list]) => (
          <optgroup key={g} label={g}>
            {list.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <span className={`preset-current ${modified ? 'modified' : ''}`} title={displayName}>
        {displayName}
        {modified ? ' *' : ''}
      </span>
    </label>
  );
}

export function SettingsPanel() {
  const settings = useAppStore((s) => s.settings);
  const visibility = useAppStore((s) => s.visibility);
  const selectedId = useAppStore((s) => s.selectedId);
  const objects = useAppStore((s) => s.objects);
  const processPresetId = useAppStore((s) => s.processPresetId);
  const filamentPresetId = useAppStore((s) => s.filamentPresetId);
  const printerPresetId = useAppStore((s) => s.printerPresetId);
  const theme = useAppStore((s) => s.theme);
  const [query, setQuery] = useState('');
  const [userPresets, setUserPresets] = useState(loadUserPresets);
  const selected = objects.find((o) => o.id === selectedId);

  const processPreset = findProcessPreset(processPresetId, userPresets);
  const filamentPreset = findFilamentPreset(filamentPresetId, userPresets);
  const printerPreset = findPrinterPreset(printerPresetId);

  const procKeys = useMemo(() => (processPreset ? processKeysOf(processPreset) : []), [processPreset]);
  const filKeys = useMemo(() => (filamentPreset ? filamentKeysOf(filamentPreset) : []), [filamentPreset]);
  const procDirty = useMemo(() => dirtyKeys(settings, processPreset, procKeys), [settings, processPreset, procKeys]);
  const filDirty = useMemo(() => dirtyKeys(settings, filamentPreset, filKeys), [settings, filamentPreset, filKeys]);
  const dirtySet = useMemo(() => new Set([...procDirty, ...filDirty]), [procDirty, filDirty]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SETTINGS.filter((s) => {
      if (!settingsVisible(visibility, s)) return false;
      if (!q) return true;
      return (
        s.label.toLowerCase().includes(q) ||
        s.key.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
      );
    });
  }, [query, visibility]);

  const byCat = useMemo(() => {
    const map = new Map<string, SettingDef[]>();
    for (const c of CATEGORIES) map.set(c, []);
    for (const s of filtered) map.get(s.category)?.push(s);
    return map;
  }, [filtered]);

  function applyProcess(id: string) {
    const p = findProcessPreset(id, userPresets);
    if (!p) return;
    store.applyProcessPreset(p);
    toast(`Applied ${p.name}`);
  }

  function applyFilament(id: string) {
    const p = findFilamentPreset(id, userPresets);
    if (!p) return;
    store.applyFilamentPreset(p);
    toast(`Applied ${p.name}`);
  }

  function applyPrinter(id: string) {
    const p = findPrinterPreset(id);
    if (!p) return;
    store.applyPrinterPreset(p);
    toast(`Printer: ${p.name}`);
  }

  function saveAs() {
    const name = prompt('Save process preset as…', `${processPreset?.name || 'Custom'} (copy)`);
    if (!name) return;
    const p: Preset = {
      id: `user-${Date.now()}`,
      kind: 'process',
      name,
      values: { ...settings },
    };
    const next = saveUserPreset(p);
    setUserPresets(next);
    store.set({ processPresetId: p.id });
    toast(`Saved “${name}”`);
  }

  const processOptions = [
    ...PROCESS_PRESETS.map((p) => ({ id: p.id, name: p.name, group: 'Bambu P1S' })),
    ...userPresets
      .filter((p) => p.kind === 'process')
      .map((p) => ({ id: p.id, name: p.name, group: 'Saved' })),
  ];
  const filamentOptions = [
    ...FILAMENT_PRESETS.map((p) => ({ id: p.id, name: p.name, group: 'Bambu' })),
    ...userPresets
      .filter((p) => p.kind === 'filament')
      .map((p) => ({ id: p.id, name: p.name, group: 'Saved' })),
  ];

  return (
    <aside className="settings-panel" data-testid="settings-panel">
      <header className="panel-header">
        <div className="panel-title-row">
          <h2>Settings</h2>
          <label className="theme-toggle" title="Theme">
            <span className="sr-only">Theme</span>
            <select
              data-testid="theme-select"
              value={theme}
              onChange={(e) => store.set({ theme: e.target.value as 'light' | 'dark' | 'auto' })}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </label>
        </div>

        <div className="preset-stack" data-testid="preset-stack">
          <PresetSelect
            label="Process preset"
            testId="process-preset"
            valueId={processPresetId}
            displayName={processPreset?.name || 'Custom'}
            modified={procDirty.length > 0}
            options={processOptions}
            onPick={applyProcess}
          />
          <PresetSelect
            label="Filament preset"
            testId="filament-preset"
            valueId={filamentPresetId}
            displayName={filamentPreset?.name || 'Custom'}
            modified={filDirty.length > 0}
            options={filamentOptions}
            onPick={applyFilament}
          />
          <PresetSelect
            label="Printer"
            testId="printer-preset"
            valueId={printerPresetId}
            displayName={printerPreset?.name || 'Bambu Lab P1S 0.4 nozzle'}
            modified={false}
            options={PRINTER_PRESETS.map((p) => ({ id: p.id, name: p.name, group: 'Bambu Lab' }))}
            onPick={applyPrinter}
          />
          <div className="preset-actions">
            <button type="button" data-testid="preset-save-as" onClick={saveAs}>
              Save as…
            </button>
            {userPresets
              .filter((p) => p.id === processPresetId)
              .map((p) => (
                <span key={p.id} className="user-preset-ops">
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      const name = prompt('Rename preset', p.name);
                      if (!name) return;
                      setUserPresets(renameUserPreset(p.id, name));
                      toast(`Renamed to “${name}”`);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      if (!confirm(`Delete “${p.name}”?`)) return;
                      setUserPresets(deleteUserPreset(p.id));
                      applyProcess('proc-0.20-standard');
                    }}
                  >
                    Delete
                  </button>
                </span>
              ))}
          </div>
        </div>

        <div className="vis-toggle" role="tablist">
          {(['basic', 'advanced', 'expert'] as Visibility[]).map((v) => (
            <button
              key={v}
              role="tab"
              className={visibility === v ? 'active' : ''}
              onClick={() => store.setVisibility(v)}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <input
          className="search"
          placeholder="Search settings…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="settings-search"
        />
      </header>

      {selected && (
        <section className="per-object">
          <h3>Object overrides · {selected.name}</h3>
          <p className="hint">Infill, walls, layer height, supports</p>
          {SETTINGS.filter((s) => s.perObject).map((def) => {
            const overridden = def.id in selected.overrides;
            const value = overridden ? selected.overrides[def.id] : settings[def.id];
            return (
              <div key={def.id} className={`override-row ${overridden ? 'on' : ''}`}>
                <SettingRow
                  def={def}
                  value={value}
                  onChange={(v) => store.setObjectOverride(selected.id, def.id, v)}
                />
                {overridden && (
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => store.setObjectOverride(selected.id, def.id, undefined)}
                  >
                    Reset
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}

      <div className="settings-tree">
        {[...byCat.entries()].map(([cat, list]) =>
          list.length === 0 ? null : (
            <details key={cat} open={cat === 'Quality' || cat === 'Supports' || cat === 'Material' || !!query}>
              <summary>
                {cat} <span className="count">{list.length}</span>
              </summary>
              <div className="setting-list">
                {list.map((def) => (
                  <SettingRow
                    key={def.id}
                    def={def}
                    value={settings[def.id]}
                    dirty={dirtySet.has(def.id)}
                    onReset={() => {
                      if (filKeys.includes(def.id)) store.resetSettingToFilament(def.id);
                      else store.resetSettingToProcess(def.id);
                    }}
                    onChange={(v) => store.setSetting(def.id, v)}
                  />
                ))}
              </div>
            </details>
          )
        )}
      </div>
    </aside>
  );
}
